/**
 * One answer for "what may this bank box show, and why is it empty?"
 *
 * Chris, 2026-09-17 (ledger row 117): operators choose the bank account on the
 * documents they raise from a delivery -- our account on customer documents,
 * the supplier's on supplier invoices. The server decides which accounts are
 * offered (invoicing/bank_account_rules.py) and sends two things with the list:
 *
 *   auto_pick_id -> the account the box may fill in by itself: the one main
 *                   account, or the only account. Null when the person must
 *                   choose. The screens used to take "the main one" and the
 *                   server "the main one or else the oldest" -- on live no
 *                   account is marked main, so a USD invoice printed the AED
 *                   account nobody chose.
 *   problem      -> why there is nothing to choose from, in words ("not linked
 *                   to its partner record yet", "switched off in CRM").
 *
 * Four states, kept apart in one place so the five screens cannot drift:
 *
 *   withheld -> "you are not allowed to see this" (say so, and say who fills it in)
 *   failed   -> "we could not load it" (say so; do not imply the list is empty)
 *   notice   -> the server explained why there is nothing to choose (show it)
 *   empty    -> genuinely no accounts on file
 */

/** HTTP statuses that mean "the server said no", not "the server broke". */
const REFUSAL_STATUSES = [401, 403];

/**
 * Classify a failed banking-details load.
 *
 * @param  {Error} error  the rejection from the service call
 * @return {{withheld: boolean, message: string}}
 */
export const describeBankLoadFailure = (error) => {
  const status = error?.response?.status;
  if (REFUSAL_STATUSES.includes(status)) {
    return {
      withheld: true,
      message:
        'Bank account details are not available to your account. Save this and '
        + 'Finance will add the bank account before it is approved.',
    };
  }
  return {
    withheld: false,
    message:
      'The bank account list could not be loaded. This is not the same as '
      + 'there being none on file — try again, and tell IT if it keeps '
      + 'happening.',
  };
};

/**
 * The text for the dropdown's own first option, so an unusable picker never
 * silently reads as "there is nothing here".
 *
 * @param {object}  state
 * @param {boolean} state.hasParent   supplier / entity chosen yet?
 * @param {string}  state.parentLabel what to ask for when it has not been
 * @param {boolean} state.withheld    the server refused this reader
 * @param {boolean} state.failed      the load errored for another reason
 * @param {number}  state.count       accounts actually loaded
 */
export const bankPickerPlaceholder = ({
  hasParent,
  parentLabel = 'Supplier',
  withheld = false,
  failed = false,
  count = 0,
}) => {
  if (!hasParent) return `Select ${parentLabel} first...`;
  if (withheld) return 'Not available to you — Finance adds this';
  if (failed) return 'Could not load bank accounts';
  if (!count) return 'No bank accounts available';
  return 'Select Bank Account...';
};

/**
 * The server's explanation for an empty list, in the same shape as a load
 * failure so the screens render it through one path -- grey, not an alert.
 *
 * @param  {object} response  the banking-details payload
 * @return {{withheld: false, notice: true, message: string} | null}
 */
export const describeBankListNotice = (response) => {
  const problem = response?.problem;
  if (!problem) return null;
  return { withheld: false, notice: true, message: problem };
};

/**
 * The account the box may fill in by itself. The server's answer, never a
 * guess made here: no main account and several on file means nothing.
 *
 * @param  {object} response  the banking-details payload
 * @return {number|null}
 */
export const autoPickBankId = (response) => {
  const id = response?.auto_pick_id;
  const listed = (response?.banking_details || []).some((bank) => bank.id === id);
  return id != null && listed ? id : null;
};

/**
 * The pledge bank a document's bank box is locked to, or null. Pass it to
 * fetchBankingDetails so the list holds only that bank's accounts (the save
 * refuses any other: invoicing/bank_account_rules.py).
 *
 * @param  {object} doc  the document's header data
 * @return {string|null}
 */
export const pledgeBankKeyOf = (doc) => (
  doc?.is_pledged && doc?.pledge_bank_key ? doc.pledge_bank_key : null
);

/**
 * What the bank box should hold once a list has loaded. Unpledged: the old
 * rule (keep the choice, fill an empty box only with the server's pick).
 * Pledged: a choice that is not in the narrowed list belongs to another bank,
 * so it is replaced by the server's pick among the pledge bank's accounts, or
 * emptied.
 *
 * @param  {object}  response   the banking-details payload
 * @param  {*}       currentId  the account in the box now
 * @param  {string}  pledgeKey  pledgeBankKeyOf(doc)
 * @return {*} the id for the box (unchanged when nothing should move)
 */
export const settleBankAccount = (response, currentId, pledgeKey) => {
  const pickId = autoPickBankId(response);
  if (!currentId) return pickId || currentId;
  if (!pledgeKey) return currentId;
  const listed = (response?.banking_details || []).some(
    (bank) => String(bank.id) === String(currentId),
  );
  return listed ? currentId : (pickId || null);
};

/**
 * The line under a pledged bank box, or null when the document is not pledged.
 *
 * @param  {object} response  the banking-details payload
 * @return {string|null}
 */
export const describePledgeLock = (response) => {
  const name = response?.pledge?.name;
  return name ? `Pledged to ${name}: only ${name} accounts can be used.` : null;
};

export default {
  describeBankLoadFailure, bankPickerPlaceholder, describeBankListNotice, autoPickBankId,
  pledgeBankKeyOf, settleBankAccount, describePledgeLock,
};
